import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {assignJobWorker} from "../../service/JobService";
import loadSummaryCountMap from './loadSummaryCountMap';
import {createIssueSummaryTracker} from '../propertyissue/propertyIssueSummary';
import {
    buildStatusCounts,
    enrichJobsWithSummaryCounts,
    jobMatchesSearch,
    sortSchedulerJobs,
} from "./schedulerUtils";

import {importantRevision, matchesJobStatus, needsAcknowledgement} from "../../service/jobScheduling";
import {replaceJobSnapshot} from "../../service/jobSnapshot";

const ALL_REGIONS = [];

async function readAssignmentError(response) {
    const fallback = response.status === 409
        ? `Job changed. Review the latest change before assigning (HTTP ${response.status}).`
        : `Unable to assign worker (HTTP ${response.status}).`;
    try {
        const body = await response.json();
        const message = [body?.detail, body?.reason, body?.message]
            .find(value => typeof value === "string" && value.trim());
        return message?.trim() || fallback;
    } catch {
        return fallback;
    }
}

function readStoredValue(primaryKey, fallbackKey, defaultValue) {
    if (!primaryKey && !fallbackKey) {
        return defaultValue;
    }

    const storedValue = localStorage.getItem(primaryKey);
    if (storedValue) {
        return storedValue;
    }

    if (fallbackKey) {
        const fallbackValue = localStorage.getItem(fallbackKey);
        if (fallbackValue) {
            return fallbackValue;
        }
    }

    return defaultValue;
}

export default function useSchedulerBoardData({
    boardConfig,
    selectedDate,
    displayedDate = selectedDate,
    searchText,
    fieldWorkerMap,
    fieldSupervisorMap,
    selectedRegions = ALL_REGIONS,
}) {
    const [taskType, setTaskType] = useState('ALL');
    const [jobs, setJobs] = useState([]);
    const [loadedContext, setLoadedContext] = useState(null);
    const [hasLoadError, setHasLoadError] = useState(false);
    const requestSequenceRef = useRef(0);
    const refreshInFlightRef = useRef(false);
    const assignmentInFlightRef = useRef(false);
    const [issueSummaryTracker] = useState(createIssueSummaryTracker);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isAssigning, setIsAssigning] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedStatus, setSelectedStatus] = useState(() => readStoredValue(
        boardConfig.statusStorageKey,
        boardConfig.legacyStatusStorageKey,
        boardConfig.defaultStatus,
    ));
    const [activeWorkerId, setActiveWorkerId] = useState(() => readStoredValue(
        boardConfig.workerStorageKey,
        null,
        boardConfig.defaultWorkerId,
    ));

    const requestDate = boardConfig.usesSelectedDate ? selectedDate : undefined;
    const requestScope = JSON.stringify([requestDate || null, selectedRegions]);
    const refreshDependency = boardConfig.usesSelectedDate ? selectedDate : boardConfig.mode;
    const hasLoaded = loadedContext !== null;
    const isScopeCurrent = loadedContext?.scope === requestScope;
    const isPending = isRefreshing || isAssigning || (!hasLoadError && !isScopeCurrent);
    const isStale = hasLoadError || (hasLoaded && !isScopeCurrent);
    const workerDate = boardConfig.usesSelectedDate ? loadedContext?.date || selectedDate : displayedDate;

    const refreshBoard = useCallback(async () => {
        const sequence = ++requestSequenceRef.current;
        const summaryRevision = issueSummaryTracker.getRevision();
        refreshInFlightRef.current = true;
        setIsRefreshing(true);
        setErrorMessage("");
        setHasLoadError(false);

        try {
            const response = await boardConfig.fetchJobs(requestDate, selectedRegions);
            if (response.status !== 200) {
                const responseText = await response.text();
                if (sequence !== requestSequenceRef.current) return;
                setHasLoadError(true);
                setErrorMessage(responseText || "Unable to load scheduler data.");
                return;
            }

            const jobResult = await response.json();
            const scopedJobs = boardConfig.filterJobs ? boardConfig.filterJobs(jobResult) : jobResult;
            const summaryCountMap = await loadSummaryCountMap(scopedJobs);
            if (sequence !== requestSequenceRef.current) return;
            setJobs(issueSummaryTracker.apply(enrichJobsWithSummaryCounts(scopedJobs, summaryCountMap), summaryRevision));
            setLoadedContext({scope: requestScope, date: requestDate});
        } catch (error) {
            if (sequence !== requestSequenceRef.current) return;
            console.log("error", error);
            setHasLoadError(true);
            setErrorMessage("Unable to load scheduler data.");
        } finally {
            if (sequence === requestSequenceRef.current) {
                refreshInFlightRef.current = false;
                setIsRefreshing(false);
            }
        }
    }, [boardConfig, requestDate, requestScope, selectedRegions, issueSummaryTracker]);

    useEffect(() => {
        refreshBoard();
        return () => { requestSequenceRef.current += 1; };
    }, [refreshBoard, refreshDependency]);

    useEffect(() => {
        if (boardConfig.statusStorageKey) {
            localStorage.setItem(boardConfig.statusStorageKey, selectedStatus);
        }
    }, [boardConfig.statusStorageKey, selectedStatus]);

    useEffect(() => {
        if (boardConfig.workerStorageKey) {
            localStorage.setItem(boardConfig.workerStorageKey, activeWorkerId);
        }
    }, [activeWorkerId, boardConfig.workerStorageKey]);

    const visibleBacklogJobs = useMemo(() => {
        const filteredJobs = jobs.filter((job) => {
            const matchesStatus = boardConfig.matchesStatus
                ? boardConfig.matchesStatus(job, selectedStatus)
                : matchesJobStatus(job, selectedStatus);
            return (taskType === "ALL" || (job.task_subtype || "GENERAL") === taskType) && matchesStatus && jobMatchesSearch(job, searchText, fieldWorkerMap, fieldSupervisorMap);
        });
        return sortSchedulerJobs(filteredJobs);
    }, [boardConfig, fieldSupervisorMap, fieldWorkerMap, jobs, searchText, selectedStatus, taskType]);

    const workerVisibleJobs = useMemo(() => {
        if (!boardConfig.filterWorkerJobs) {
            return jobs;
        }

        return jobs.filter((job) => boardConfig.filterWorkerJobs(job, {selectedDate: workerDate}));
    }, [boardConfig, jobs, workerDate]);

    const workerJobs = useMemo(() => {
        const filteredJobs = workerVisibleJobs.filter((job) => {
            return Boolean(activeWorkerId) && String(job.cos_job_worker) === String(activeWorkerId) && job.status !== "Cancelled";
        });
        return sortSchedulerJobs(filteredJobs);
    }, [activeWorkerId, workerVisibleJobs]);

    const statusCounts = useMemo(
        () => buildStatusCounts(jobs, boardConfig.statusKey),
        [boardConfig.statusKey, jobs],
    );
    const updateJobInState = useCallback((updatedJob) => {
        setJobs((previousJobs) => previousJobs.map((job) => {
            if (job.cos_job_id !== updatedJob.cos_job_id) {
                return job;
            }

            return replaceJobSnapshot(job, updatedJob);
        }));
    }, []);

    const assignWorker = useCallback(async (jobId, workerId, jobOverride = null) => {
        if (refreshInFlightRef.current || assignmentInFlightRef.current || isPending || isStale) return false;
        assignmentInFlightRef.current = true;
        setIsAssigning(true);
        try {
            const currentJob = jobOverride || jobs.find((job) => String(job.cos_job_id) === String(jobId));
            const response = currentJob && boardConfig.assignWorker
                ? await boardConfig.assignWorker(currentJob, workerId, {selectedDate})
                : await assignJobWorker(jobId, workerId, workerId && needsAcknowledgement(currentJob) ? importantRevision(currentJob) : undefined);
            if (response.status !== 200) {
                const assignmentError = await readAssignmentError(response);
                if (response.status === 409) await refreshBoard();
                setErrorMessage(assignmentError);
                return false;
            }
            const updatedJob = await response.json();
            setJobs((previousJobs) => previousJobs.map((job) => {
                if (String(job.cos_job_id) !== String(jobId)) {
                    return job;
                }
                return replaceJobSnapshot(job, updatedJob);
            }));
            return true;
        } catch (error) {
            console.log("error", error);
            setErrorMessage("Unable to assign worker.");
            return false;
        } finally {
            assignmentInFlightRef.current = false;
            setIsAssigning(false);
        }
    }, [boardConfig, jobs, selectedDate, refreshBoard, isPending, isStale]);

    const updateIssueSummary = useCallback((propertyId, summary) => {
        const revision = issueSummaryTracker.getRevision();
        issueSummaryTracker.record(propertyId, summary);
        setJobs(previous => issueSummaryTracker.apply(previous, revision));
    }, [issueSummaryTracker]);

    return {
        taskType, setTaskType,
        activeWorkerId,
        assignWorker,
        displayedDate: workerDate,
        errorMessage,
        hasLoaded,
        hasLoadError,
        isLoading: isPending,
        isStale,
        jobs,
        refreshBoard,
        selectedStatus,
        setActiveWorkerId,
        setSelectedStatus,
        statusCounts,
        updateJobInState,
        updateIssueSummary,
        visibleBacklogJobs,
        workerVisibleJobs,
        workerJobs,
    };
}
